// src/pages/RestaurantEditPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { SUPPORTED_CURRENCIES } from '../config/currencies';
import LoadingSpinner from '../components/loadingspinner';
import { trackAdminAction } from '../services/analyticsService'; // Import analytics function
import LocationPicker from '../components/LocationPicker'; // Import LocationPicker component

function RestaurantEditPage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Restaurant Edit State
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    currency: '',
    logoUrl: '',
    location: { address: '', latitude: null, longitude: null },
    onlinePricingAdjustmentType: 'percent',
    onlinePricingAdjustmentValue: 0,
    onlinePricingAdjustmentType: 'percent',
    onlinePricingAdjustmentValue: 0,
    tablePricingAdjustmentType: 'percent',
    tablePricingAdjustmentValue: 0,
    seatCategories: []
  });
  // New state for key-based re-rendering or temp storage if needed, but formData should suffice.
  const [newSeatArea, setNewSeatArea] = useState({ name: '', type: 'percent', value: 0 });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        setError('');
        const restaurantDocRef = doc(db, 'restaurants', restaurantId);
        const restaurantSnap = await getDoc(restaurantDocRef);

        if (!restaurantSnap.exists()) {
          setError('Restaurant not found.');
          setLoading(false);
          return;
        }

        const restaurantData = { id: restaurantSnap.id, ...restaurantSnap.data() };
        setRestaurant(restaurantData);
        setFormData({
          name: restaurantData.name || '',
          slug: restaurantData.slug || '',
          currency: restaurantData.currency || SUPPORTED_CURRENCIES[0].code,
          logoUrl: restaurantData.logoUrl || '',
          location: restaurantData.location || { address: '', latitude: null, longitude: null },
          onlinePricingAdjustmentType: restaurantData.onlinePricingAdjustmentType || 'percent',
          onlinePricingAdjustmentValue: typeof restaurantData.onlinePricingAdjustmentValue === 'number' ? restaurantData.onlinePricingAdjustmentValue : 0,
          tablePricingAdjustmentType: restaurantData.tablePricingAdjustmentType || 'percent',
          onlinePricingAdjustmentValue: typeof restaurantData.onlinePricingAdjustmentValue === 'number' ? restaurantData.onlinePricingAdjustmentValue : 0,
          tablePricingAdjustmentType: restaurantData.tablePricingAdjustmentType || 'percent',
          tablePricingAdjustmentValue: typeof restaurantData.tablePricingAdjustmentValue === 'number' ? restaurantData.tablePricingAdjustmentValue : 0,
          tablePricingAdjustmentValue: typeof restaurantData.tablePricingAdjustmentValue === 'number' ? restaurantData.tablePricingAdjustmentValue : 0,
          seatCategories: (Array.isArray(restaurantData.seatCategories) && restaurantData.seatCategories.length > 0)
            ? restaurantData.seatCategories 
            : [{ id: 'default-std', name: 'Standard', type: 'percent', value: 0 }]
        });
        setLogoPreview(restaurantData.logoUrl || null);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching restaurant data:', err);
        setError('Failed to load restaurant data. Please try again.');
        setLoading(false);
      }
    };

    if (restaurantId) {
      fetchRestaurantData();
    }
  }, [restaurantId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Logo file size should not exceed 5MB.');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.name || !formData.slug || !formData.currency) {
      setError('Restaurant name, URL ID (slug), and currency are required.');
      return;
    }

    // Validate location
    if (!formData.location.address || !formData.location.latitude || !formData.location.longitude) {
      setError('Restaurant location is required. Please select a location.');
      return;
    }

    setIsUpdating(true);
    setUploadProgress(0);

    try {
      let updatedLogoUrl = formData.logoUrl;

      if (logoFile) {
        const fileExtension = logoFile.name.split('.').pop();
        const uniqueFileName = `logo_${Date.now()}.${fileExtension}`;
        const logoPath = `restaurants/${restaurantId}/logo/${uniqueFileName}`;
        const logoRef = ref(storage, logoPath);
        const metadata = { contentType: logoFile.type };
        const uploadTask = uploadBytesResumable(logoRef, logoFile, metadata);

        updatedLogoUrl = await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              console.error("Logo upload failed:", error);
              setError(`Logo upload failed: ${error.message}`);
              setUploadProgress(0);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                setUploadProgress(100);
                resolve(downloadURL);
              } catch (urlError) {
                console.error("Error getting logo download URL:", urlError);
                setError(`Logo uploaded but failed to get URL: ${urlError.message}`);
                reject(urlError);
              }
            }
          );
        });
      }

      const restaurantRef = doc(db, "restaurants", restaurantId);
      await updateDoc(restaurantRef, {
        name: formData.name,
        slug: formData.slug,
        currency: formData.currency,
        logoUrl: updatedLogoUrl,
        location: {
          address: formData.location.address,
          latitude: formData.location.latitude,
          longitude: formData.location.longitude
        },
        onlinePricingAdjustmentType: formData.onlinePricingAdjustmentType,
        onlinePricingAdjustmentValue: Number(formData.onlinePricingAdjustmentValue) || 0,
        tablePricingAdjustmentType: formData.tablePricingAdjustmentType,
        onlinePricingAdjustmentValue: Number(formData.onlinePricingAdjustmentValue) || 0,
        tablePricingAdjustmentType: formData.tablePricingAdjustmentType,
        tablePricingAdjustmentValue: Number(formData.tablePricingAdjustmentValue) || 0,
        seatCategories: formData.seatCategories
      });

      setSuccessMessage('Restaurant details updated successfully!');
      setRestaurant(prev => ({ 
        ...prev, 
        name: formData.name,
        slug: formData.slug,
        currency: formData.currency,
        logoUrl: updatedLogoUrl,
        location: formData.location,
        onlinePricingAdjustmentType: formData.onlinePricingAdjustmentType,
        onlinePricingAdjustmentValue: Number(formData.onlinePricingAdjustmentValue) || 0,
        tablePricingAdjustmentType: formData.tablePricingAdjustmentType,
        onlinePricingAdjustmentValue: Number(formData.onlinePricingAdjustmentValue) || 0,
        tablePricingAdjustmentType: formData.tablePricingAdjustmentType,
        tablePricingAdjustmentValue: Number(formData.tablePricingAdjustmentValue) || 0,
        seatCategories: formData.seatCategories
      }));
      setFormData(prev => ({ ...prev, logoUrl: updatedLogoUrl }));
      setLogoFile(null);
      trackAdminAction('update_restaurant_details', { restaurantId, name: formData.name, slug: formData.slug, currency: formData.currency });
      // Optionally navigate back or show success longer
      setTimeout(() => navigate(`/edit/${restaurantId}`), 1500); // Go back to edit menu page

    } catch (error) {
      console.error("Error updating restaurant:", error);
      setError(`Update failed: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Seat Area Handlers
  const handleAddSeatArea = () => {
    if (!newSeatArea.name.trim()) return;
    
    const category = {
      id: Date.now().toString(),
      name: newSeatArea.name.trim(),
      type: newSeatArea.type,
      value: Number(newSeatArea.value) || 0
    };

    setFormData(prev => ({
      ...prev,
      seatCategories: [...prev.seatCategories, category]
    }));
    setNewSeatArea({ name: '', type: 'percent', value: 0 });
  };

  const handleDeleteSeatArea = (id) => {
    if (formData.seatCategories.length <= 1) {
      alert("You must have at least one seating area.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      seatCategories: prev.seatCategories.filter(cat => cat.id !== id)
    }));
  };

  const handleSeatAreaChangeLocal = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      seatCategories: prev.seatCategories.map(cat => 
        cat.id === id ? { ...cat, [field]: field === 'value' ? (Number(value) || 0) : value } : cat
      )
    }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container" style={{ padding: '20px', maxWidth: '700px', margin: '0 auto' }}>
      <Link to={`/dashboard/${restaurantId}`} className="btn" style={{  textDecoration: 'none',marginBottom: '20px', display: 'inline-block' }}> Back </Link>
      <h2 className="mt-3 mb-4" style={{ marginTop: '20px', marginBottom: '30px', textAlign: 'center' }}>Edit Restaurant Details</h2>
      {error && <div className="alert alert-danger" style={{ marginBottom: '20px' }}>{error}</div>}
      {successMessage && <div className="alert alert-success" style={{ marginBottom: '20px' }}>{successMessage}</div>}
      
      <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div className="mb-3" style={{ marginBottom: '15px' }}>
          <label htmlFor="restaurantName" className="form-label" style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>Restaurant Name</label>
          <input 
            type="text" 
            className="form-control" 
            id="restaurantName" 
            name="name" 
            value={formData.name} 
            onChange={handleInputChange} 
            required 
            style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
          />
        </div>
        <div className="mb-3" style={{ marginBottom: '15px' }}>
          <label htmlFor="restaurantSlug" className="form-label" style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>URL ID (Slug)</label>
          <input 
            type="text" 
            className="form-control" 
            id="restaurantSlug" 
            name="slug" 
            value={formData.slug} 
            onChange={handleInputChange} 
            required 
            pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$" // Basic slug pattern
            title="Use lowercase letters, numbers, and hyphens only (e.g., my-cool-restaurant)"
            style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
          />
          <div className="form-text" style={{ fontSize: '0.9em', color: '#6c757d', marginTop: '5px' }}>This is used in the public menu URL. Use lowercase letters, numbers, and hyphens.</div>
        </div>
        <div className="mb-3" style={{ marginBottom: '15px' }}>
          <label htmlFor="restaurantCurrency" className="form-label" style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>Currency</label>
          <select 
            className="form-select" 
            id="restaurantCurrency" 
            name="currency" 
            value={formData.currency} 
            onChange={handleInputChange} 
            required
            style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
          >
            {SUPPORTED_CURRENCIES.map(curr => (
              <option key={curr.code} value={curr.code}>
                {curr.name} ({curr.symbol})
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3" style={{ marginBottom: '20px' }}>
          <LocationPicker
            value={formData.location}
            onChange={(location) => setFormData(prev => ({ ...prev, location }))}
            required={true}
            error={error && (!formData.location.address || !formData.location.latitude || !formData.location.longitude) ? 'Location is required' : null}
          />
        </div>
        <div className="mb-3" style={{ marginBottom: '20px' }}>
          <label htmlFor="restaurantLogo" className="form-label" style={{ marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>Restaurant Logo</label>
          <input 
            type="file" 
            className="form-control" 
            id="restaurantLogo" 
            onChange={handleLogoChange} 
            accept="image/png, image/jpeg, image/gif, image/webp"
            style={{ marginBottom: '10px' }}
          />
          {logoPreview && (
            <div style={{ marginTop: '10px', marginBottom: '10px', border: '1px solid #eee', padding: '10px', borderRadius: '5px', textAlign: 'center' }}>
              <img src={logoPreview} alt="Logo Preview" style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }} />
            </div>
          )}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="progress" style={{ marginTop: '10px', height: '20px' }}>
              <div 
                className="progress-bar" 
                role="progressbar" 
                style={{ width: `${uploadProgress}%` }} 
                aria-valuenow={uploadProgress} 
                aria-valuemin="0" 
                aria-valuemax="100"
              >
                {Math.round(uploadProgress)}%
              </div>
            </div>
          )}
          <div className="form-text" style={{ fontSize: '0.9em', color: '#6c757d', marginTop: '5px' }}>Recommended: Square logo, max 5MB.</div>
        </div>

        {/* Online Ordering Section */}
        <div className="mb-3" style={{ marginBottom: '30px', border: '1px solid #eee', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>Online Ordering</h3>
          <p style={{ margin: '0 0 15px 0', color: '#6c757d' }}>Configuration for orders placed online.</p>
          
          <h4 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>Pricing Adjustment</h4>
          <p style={{ fontSize: '0.9em', color: '#6c757d', marginBottom: '10px' }}>Apply a surcharge or discount for online orders.</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>Type</label>
              <select
                name="onlinePricingAdjustmentType"
                className="form-select"
                value={formData.onlinePricingAdjustmentType}
                onChange={handleInputChange}
              >
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>Value</label>
              <input
                type="number"
                name="onlinePricingAdjustmentValue"
                className="form-control"
                step="0.01"
                value={formData.onlinePricingAdjustmentValue}
                onChange={handleInputChange}
                placeholder={formData.onlinePricingAdjustmentType === 'percent' ? 'e.g. 0 for no change' : 'e.g. 1.00'}
              />
            </div>
          </div>
        </div>

        {/* Table Booking Section */}
        <div className="mb-3" style={{ marginBottom: '20px', border: '1px solid #eee', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ marginBottom: '10px' }}>Table Booking</h3>
          <p style={{ margin: '0 0 15px 0', color: '#6c757d' }}>Configuration for dine-in orders.</p>

          <h4 style={{ fontSize: '1.1rem', marginBottom: '10px' }}>Seating Areas & Pricing</h4>
          <p style={{ fontSize: '0.9em', color: '#6c757d', marginBottom: '15px' }}>
            Define available seating areas and their specific pricing.
          </p>
          
          <div style={{ marginTop: '15px' }}>
            {formData.seatCategories.length > 0 && (
              <div style={{ marginBottom: '15px', display: 'grid', gap: '10px' }}>
                {formData.seatCategories.map(item => (
                  <div key={item.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', padding: '10px', border: '1px solid #eee', borderRadius: '5px', background: '#fafafa' }}>
                    <div style={{ flex: 2 }}>
                       <label className="form-label" style={{ fontSize: '0.85em', display: 'block', marginBottom: '4px' }}>Area Name</label>
                       <input 
                         type="text" 
                         className="form-control"
                         value={item.name}
                         onChange={(e) => handleSeatAreaChangeLocal(item.id, 'name', e.target.value)}
                       />
                    </div>
                    <div style={{ flex: 1 }}>
                       <label className="form-label" style={{ fontSize: '0.85em', display: 'block', marginBottom: '4px' }}>Type</label>
                       <select 
                         className="form-select"
                         value={item.type}
                         onChange={(e) => handleSeatAreaChangeLocal(item.id, 'type', e.target.value)}
                       >
                         <option value="percent">Percent (%)</option>
                         <option value="fixed">Fixed</option>
                       </select>
                    </div>
                    <div style={{ flex: 1 }}>
                       <label className="form-label" style={{ fontSize: '0.85em', display: 'block', marginBottom: '4px' }}>Value</label>
                       <input 
                         type="number"
                         step="0.01"
                         className="form-control"
                         value={item.value}
                         onChange={(e) => handleSeatAreaChangeLocal(item.id, 'value', e.target.value)}
                       />
                    </div>
                    <div>
                      <button 
                        type="button" 
                        className="btn btn-danger" 
                        onClick={() => handleDeleteSeatArea(item.id)}
                        disabled={formData.seatCategories.length <= 1}
                        style={{ padding: '6px 10px', cursor: formData.seatCategories.length <= 1 ? 'not-allowed' : 'pointer', opacity: formData.seatCategories.length <= 1 ? 0.6 : 1 }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Area */}
            <div style={{ border: '1px dashed #ccc', padding: '15px', borderRadius: '5px' }}>
               <h5 style={{ marginTop: 0, marginBottom: '10px' }}>Add New Seating Area</h5>
               <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 2 }}>
                     <label className="form-label" style={{ display: 'block', marginBottom: '4px' }}>Name</label>
                     <input 
                       type="text" 
                       className="form-control"
                       placeholder="e.g. VIP, Outdoor"
                       value={newSeatArea.name}
                       onChange={(e) => setNewSeatArea(prev => ({...prev, name: e.target.value}))}
                     />
                  </div>
                  <div style={{ flex: 1 }}>
                     <label className="form-label" style={{ display: 'block', marginBottom: '4px' }}>Type</label>
                     <select 
                       className="form-select"
                       value={newSeatArea.type}
                       onChange={(e) => setNewSeatArea(prev => ({...prev, type: e.target.value}))}
                     >
                       <option value="percent">Percent (%)</option>
                       <option value="fixed">Fixed</option>
                     </select>
                  </div>
                  <div style={{ flex: 1 }}>
                     <label className="form-label" style={{ display: 'block', marginBottom: '4px' }}>Value</label>
                     <input 
                       type="number" 
                       step="0.01"
                       className="form-control"
                       value={newSeatArea.value}
                       onChange={(e) => setNewSeatArea(prev => ({...prev, value: e.target.value}))}
                     />
                  </div>
                  <div>
                    <button 
                      type="button" 
                      className="btn btn-success" 
                      onClick={handleAddSeatArea}
                      disabled={!newSeatArea.name}
                    >
                      Add
                    </button>
                  </div>
               </div>
            </div>
          </div>
        </div>
        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={isUpdating}
          style={{ padding: '12px 20px', fontSize: '1.1em', borderRadius: '5px', cursor: isUpdating ? 'not-allowed' : 'pointer' }}
        >
          {isUpdating ? 'Updating...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

export default RestaurantEditPage;